import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import axios from 'axios';
import type { SoothConnector } from '../../types/sooth';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  disconnected: 'warning',
  error: 'error',
  paused: 'default',
};

const SoothConnectorsPage: React.FC = () => {
  const [connectors, setConnectors] = useState<SoothConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('whatsapp_baileys');
  const [saving, setSaving] = useState(false);

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/sooth/connectors/');
      setConnectors(response.data.results || response.data);
      setError('');
    } catch (err) {
      setError('Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectors();
  }, []);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/v1/sooth/connectors/', {
        connector_type: newType,
        label: newLabel.trim(),
        config: {},
      });
      setDialogOpen(false);
      setNewLabel('');
      fetchConnectors();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create connector');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this connector? This will disconnect it from WhatsApp.')) return;
    try {
      await axios.delete(`/api/v1/sooth/connectors/${id}/`);
      fetchConnectors();
    } catch (err) {
      setError('Failed to delete connector');
    }
  };

  const getPairUrl = (connector: SoothConnector): string => {
    return `/api/v1/sooth/pair/${connector.id}/`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4">Connectors</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={fetchConnectors} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Connector
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Connectors link your WhatsApp accounts to Sooth. Each connector represents one WhatsApp session.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : connectors.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            No connectors configured. Add a connector to start receiving WhatsApp messages.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Your First Connector
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {connectors.map((connector) => (
            <Paper key={connector.id} sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <WhatsAppIcon sx={{ color: '#25D366', fontSize: 36 }} />
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6">{connector.label}</Typography>
                      <Chip
                        label={connector.status}
                        size="small"
                        color={STATUS_COLORS[connector.status] || 'default'}
                      />
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                      {connector.connector_type === 'whatsapp_baileys' ? 'WhatsApp (Baileys)' : 'WhatsApp (Cloud API)'}
                      {connector.last_heartbeat && ` · Last active: ${new Date(connector.last_heartbeat).toLocaleString()}`}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {connector.connector_type === 'whatsapp_baileys' && connector.status !== 'active' && (
                    <Tooltip title="Open QR pairing page">
                      <Button
                        variant="outlined"
                        startIcon={<QrCodeIcon />}
                        href={getPairUrl(connector)}
                        target="_blank"
                        size="small"
                      >
                        Pair
                      </Button>
                    </Tooltip>
                  )}
                  {connector.status === 'active' && (
                    <Chip
                      icon={<WhatsAppIcon />}
                      label="Connected"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  <IconButton size="small" onClick={() => handleDelete(connector.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Connector</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. My WhatsApp"
              fullWidth
            />
            <TextField
              select
              label="Type"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              fullWidth
            >
              <MenuItem value="whatsapp_baileys">WhatsApp (Baileys — personal number)</MenuItem>
              <MenuItem value="whatsapp_cloud">WhatsApp (Cloud API — business number)</MenuItem>
            </TextField>
            {newType === 'whatsapp_baileys' && (
              <Alert severity="info">
                After creating, you'll need to pair by scanning a QR code with your WhatsApp app.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !newLabel.trim()}>
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoothConnectorsPage;
